import { Component, inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Resume } from 'src/app/models/configurators-2d/resume/resume';
import { FenceService } from 'src/app/services/configurators-2d/applications/fence.service';
import { Configurators2DComponent } from '../../configurators-2d.component';
import { FencePreviewComponent } from '../preview/preview.component';
import { FenceConfiguratorItem } from '../../configurator/applications/fence-configurator-item';
import { checkPanelCount, getTotalLength, remainingDoorNumber } from 'src/app/services/helpers/fence-helper';
import { IBaseComponent } from '../../configurator/i-base-component';
import { MaterialsFacadeService } from '../materials/services/materials.facade.service';
import { ColorStateService } from '../services/color.state.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'ng-fence-summary',
  templateUrl: './summary.component.html',
})
export class FenceSummaryComponent extends FenceConfiguratorItem implements OnInit, IBaseComponent {
  currentLang: string;

  products: {
    categoryNameFr: string;
    categoryNameEn: string;
    items: {
      nameFr: string;
      additionalInfo: string;
      quantity: number;
    }[];
  }[] = [];

  totalLength: number;

  preview: FencePreviewComponent;

  isVendor!: boolean;

  private readonly materialsFacadeService = inject(MaterialsFacadeService);
  private readonly translate = inject(TranslateService);
  protected readonly colorStateService = inject(ColorStateService);

  constructor(fenceService: FenceService, configurators2DComponent: Configurators2DComponent) {
    super(fenceService);
    this.preview = configurators2DComponent.preview as FencePreviewComponent;
    this.isVendor = environment.vendor;
  }

  /**
   * Check si une couleur est sélectionnée et n'est pas désactivée
   * Sauf si c'est un vendeur, on le laisse continuer la configuration malgré le déréférencement
   * @private
   */
  private get checkColorSelectionContainDisabled(): boolean {
    if (this.isVendor) {
      return false;
    }

    for (const configurationInstallation of this.configuration.configurationInstallations) {
      const colorIds = configurationInstallation.configurationBoards.map(b => b.boardId);

      const colors = this.colorStateService
        .colorsByInstallations()
        .get(configurationInstallation.installationId)
        .filter(c => colorIds.includes(c.id));

      if (colors.some(c => c.isDisabled)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check si un poteau est sélectionné et n'est pas désactivé
   * Sauf si c'est un vendeur, on le laisse continuer la configuration malgré le déréférencement
   * @private
   */
  private get checkPostTypeSelectionIsCorrect(): boolean {
    return this.configuration.postType && (this.isVendor || !this.configuration.postType.isDisabled);
  }

  /**
   * Check si tous les portillons sélectionnés ne sont pas désactivés et que tous les emplacements de portillon sont pris.
   * Sauf si c'est un vendeur, on le laisse continuer la configuration malgré le déréférencement
   * @private
   */
  private get checkDoorsSelectionIsCorrect(): boolean {
    return (
      remainingDoorNumber(this.configuration) === 0 &&
      this.configuration.doors != undefined &&
      (this.isVendor || this.configuration.doors.every(d => !d.isDisabled))
    );
  }

  ngOnInit(): void {
    this.currentLang = this.translate.getBrowserLang();
    this.configuration.isComplete = true;
    this.totalLength = getTotalLength(this.configuration);
    this.service.getResume<Resume>().then(async () => {
      this.constructSummaryProducts();
      await this.preview.constructResumeGroups();
    });
  }

  checkPageChangeCapability(): boolean {
    const checkDecorativeElementsCompleted = this.materialsFacadeService.checkDecorativeElementsCompleted(
      this.configuration
    );

    return (
      this.configuration.configurationInstallations.length > 0 &&
      this.configuration.configurationInstallations.every(i => i.configurationBoards.length > 0) &&
      !this.checkColorSelectionContainDisabled &&
      this.checkPostTypeSelectionIsCorrect &&
      checkDecorativeElementsCompleted &&
      this.checkDoorsSelectionIsCorrect &&
      checkPanelCount(this.configuration)
    );
  }

  isNextButtonActive(): boolean {
    return true;
  }

  /**
   * Fonction de construction des produits pour le résumé
   * @returns tableau de produits
   */
  private constructSummaryProducts() {
    if (this.configuration.resume) {
      this.configuration.resume.products.forEach(product => {
        if (this.products.find(p => p.categoryNameEn === product.categoryNameEn)) {
          this.products[this.products.findIndex(p => p.categoryNameEn === product.categoryNameEn)].items.push({
            nameFr: product.nameFr,
            additionalInfo: product.additionalInfo,
            quantity: product.quantity,
          });
        } else {
          this.products.push({
            categoryNameFr: product.categoryNameFr,
            categoryNameEn: product.categoryNameEn,
            items: [
              {
                nameFr: product.nameFr,
                additionalInfo: product.additionalInfo,
                quantity: product.quantity,
              },
            ],
          });
        }
      });
    }
  }
}
